import { useCookieConsent } from "@/hooks/use-cookie-consent";
import BrandLogo from "@/components/layout/brand-logo";
import {softwareNavLabel,softwarePath} from "../../../shared/software-copy";
import { api } from "@/convex/_generated/api";
import { localizedPath, type RouteKey } from "@/lib/routes";
import {
  readInitialPublicBranding,
  resolvePublicBranding,
} from "@/lib/public-branding";
import { cn } from "@/lib/utils";
import { ChevronDown, LifeBuoy, Mail, MapPin, Phone } from "lucide-react";
import { useDeferredPublicEnhancement } from "@/hooks/use-deferred-public-enhancement";
import { useQuery } from "convex/react";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { useId, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

const AGENCY_LINKS: { key: RouteKey; label: string }[] = [
  { key: "about", label: "nav.about" },
  { key: "careers", label: "nav.careers" },
  { key: "services", label: "nav.services" },
  { key: "editorial", label: "nav.editorial" },
  { key: "tools", label: "nav.tools" },
];

const NETWORK_LINKS: { key: RouteKey; label: string }[] = [
  { key: "sponsored", label: "nav.sponsored" },
  { key: "partners", label: "nav.partners" },
  { key: "blog", label: "nav.blog" },
  { key: "shop", label: "nav.shop" },
];

const LEGAL: { key: RouteKey; label: string }[] = [
  { key: "terms", label: "footer.terms" },
  { key: "withdrawal", label: "footer.withdrawal" },
  { key: "privacy", label: "footer.privacy" },
  { key: "cookie", label: "footer.cookie" },
];

const DEFAULT_COPYRIGHT =
  "© 2026 - ENKEY SNC - Tutti i diritti riservati - P.IVA IT03202450924 - Cod. REA CA253701 - DUNS 432290442";

function FooterDisclosure({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const contentId = useId();

  return (
    <section className="border-t border-border lg:border-0">
      <h3 aria-label={title} className="font-display text-sm font-semibold">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((value) => !value)}
          className="flex min-h-14 w-full items-center justify-between gap-3 text-left lg:hidden"
        >
          <span>{title}</span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "size-4 shrink-0 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
        <span className="hidden lg:block">{title}</span>
      </h3>
      <div
        id={contentId}
        className={cn(open ? "block" : "hidden", "pb-6 lg:block lg:pb-0")}
      >
        {children}
      </div>
    </section>
  );
}

function SocialGlyph({
  network,
}: {
  network: "Facebook" | "Instagram" | "LinkedIn" | "X" | "WhatsApp";
}) {
  const iconClass = "size-[18px]";
  if (network === "Facebook")
    return <FaFacebookF aria-hidden="true" className={iconClass} />;
  if (network === "Instagram")
    return <FaInstagram aria-hidden="true" className={iconClass} />;
  if (network === "LinkedIn")
    return <FaLinkedinIn aria-hidden="true" className={iconClass} />;
  if (network === "X")
    return <FaXTwitter aria-hidden="true" className={iconClass} />;
  return <FaWhatsapp aria-hidden="true" className={iconClass} />;
}

function socialBrandClass(
  network: "Facebook" | "Instagram" | "LinkedIn" | "X" | "WhatsApp",
) {
  if (network === "Facebook")
    return "border-transparent bg-[#1877F2] text-white";
  if (network === "Instagram")
    return "border-transparent bg-[linear-gradient(135deg,#FEDA75_0%,#FA7E1E_25%,#D62976_50%,#962FBF_75%,#4F5BD5_100%)] text-white";
  if (network === "LinkedIn")
    return "border-transparent bg-[#0A66C2] text-white";
  if (network === "WhatsApp")
    return "border-transparent bg-[#25D366] text-white";
  return "border-transparent bg-black text-white";
}

export default function SiteFooter() {
  const { t, i18n } = useTranslation("common");
  const { lng } = useParams<{ lng: string }>();
  const locale = lng ?? (["it","en","es","fr","de"].includes(i18n.language) ? i18n.language : "it");
  const enhancementsReady = useDeferredPublicEnhancement();
  const initialBranding = useMemo(() => readInitialPublicBranding(), []);
  const liveBranding = useQuery(
    api.crm.branding.getPublic,
    enhancementsReady && !initialBranding ? {} : "skip",
  );
  const branding = resolvePublicBranding(initialBranding, liveBranding);
  const { openPreferences } = useCookieConsent();
  const configuredCopyright = branding?.copyrightText?.trim();
  const copyrightText =
    configuredCopyright && configuredCopyright !== DEFAULT_COPYRIGHT
      ? configuredCopyright
      : t("footer.copyright");
  const socialLinks = [
    {
      name: "Facebook",
      url: branding?.facebookUrl,
    },
    {
      name: "Instagram",
      url: branding?.instagramUrl,
    },
    {
      name: "LinkedIn",
      url: branding?.linkedinUrl,
    },
    {
      name: "X",
      url: branding?.xUrl,
    },
    {
      name: "WhatsApp",
      url: "https://wa.me/393701063366",
    },
  ] as const;

  return (
    <footer data-print-hidden className="border-t border-border bg-muted/30">
      <div className="mx-auto max-w-[1600px] px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-0 lg:grid-cols-3 lg:gap-10 xl:grid-cols-[minmax(15rem,1.35fr)_repeat(4,minmax(0,1fr))]">
          <div className="pb-8 lg:pb-0">
            <Link
              to={localizedPath("home", locale)}
              className="inline-flex items-center"
            >
              <BrandLogo
                src={branding?.footerLogoUrl || branding?.logoUrl}
                displayWidth={branding?.footerLogoWidth}
                intrinsicWidth={branding?.footerLogoIntrinsicWidth}
                intrinsicHeight={branding?.footerLogoIntrinsicHeight}
                className="h-auto max-w-full rounded-xl"
              />
            </Link>
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
          </div>

          <FooterDisclosure title={t("footer.agency")}>
            <nav aria-label={t("footer.agency")}>
              <ul className="space-y-2 lg:mt-4">
                {AGENCY_LINKS.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={localizedPath(item.key, locale)}
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {t(item.label)}
                    </Link>
                  </li>
                ))}
                <li><a href={softwarePath(locale)} className="text-sm text-muted-foreground transition-colors hover:text-primary">{softwareNavLabel[locale]??"Software"}</a></li>
              </ul>
            </nav>
          </FooterDisclosure>

          <FooterDisclosure title={t("footer.network")}>
            <nav aria-label={t("footer.network")}>
              <ul className="space-y-2 lg:mt-4">
                {NETWORK_LINKS.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={localizedPath(item.key, locale)}
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {t(item.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </FooterDisclosure>

          <FooterDisclosure title={t("footer.legal")}>
            <nav aria-label={t("footer.legal")}>
              <ul className="space-y-2 lg:mt-4">
                {LEGAL.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={localizedPath(item.key, locale)}
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {t(item.label)}
                    </Link>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    onClick={openPreferences}
                    className="cursor-pointer text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {t("cookie.manage")}
                  </button>
                </li>
              </ul>
            </nav>
          </FooterDisclosure>

          <FooterDisclosure title={t("footer.contact")}>
            <ul className="mt-4 space-y-3">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <LifeBuoy className="h-4 w-4 text-primary" />
                <Link
                  to={localizedPath("contact", locale)}
                  className="hover:text-primary"
                >
                  {t("nav.support")}
                </Link>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 text-primary" />
                <a href="mailto:info@enkey.eu" className="hover:text-primary">
                  info@enkey.eu
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary" />
                {t("footer.address")}
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 text-primary" />
                <a href="tel:+39078162719" className="hover:text-primary">
                  +39 0781 62719
                </a>
              </li>
            </ul>
            <div className="mt-5 flex flex-nowrap gap-1.5">
              {socialLinks.map((social) => {
                const className =
                  "grid size-9 shrink-0 place-items-center rounded-lg border shadow-sm transition-all";
                const brandClass = socialBrandClass(social.name);
                const mark = <SocialGlyph network={social.name} />;
                return social.url ? (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    title={social.name}
                    className={`${className} ${brandClass} hover:-translate-y-0.5 hover:brightness-110 hover:shadow-md`}
                  >
                    {mark}
                  </a>
                ) : (
                  <span
                    key={social.name}
                    aria-label={t("footer.social_not_configured", {
                      network: social.name,
                    })}
                    aria-disabled="true"
                    title={t("footer.social_not_configured", {
                      network: social.name,
                    })}
                    className={`${className} ${brandClass} cursor-not-allowed opacity-40 grayscale-[35%]`}
                  >
                    {mark}
                  </span>
                );
              })}
            </div>
          </FooterDisclosure>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-2 border-t border-border pt-6 text-center text-xs text-muted-foreground sm:flex-row sm:text-left">
          <p>{copyrightText}</p>
          <p>{t("footer.made")}</p>
        </div>
      </div>
    </footer>
  );
}
